import { pickRouteEndpoints } from '#/entities/shipment'
import {
  EMPTY_LOCATION_DEFAULTS,
  EMPTY_SHIPMENT_DEFAULTS,
  EMPTY_VEHICLES_DEFAULTS,
  EMPTY_VEHICLE,
  normalizeLocationType,
  normalizeVehicleType,
} from '#/features/order-create'
import { usPhoneDigits } from '#/shared/lib/format'
import type {
  LocationData,
  LocationFormData,
  ShipmentFormData,
  VehicleItem,
  VehiclesFormData,
} from '#/features/order-create'
import type {
  ShipmentDetail,
  StopResponse,
  VehicleResponse,
} from '#/shared/api/generated/shipment/models'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

function vehicleToFormItem(vehicle: VehicleResponse): VehicleItem {
  return {
    id: vehicle.id,
    year: String(vehicle.year),
    make: vehicle.make,
    model: vehicle.model,
    type: normalizeVehicleType(vehicle.type),
    inop: vehicle.is_inoperable,
    lot: vehicle.lot ?? '',
    vin: vehicle.vin ?? '',
  }
}

export function vehiclesToFormDefaults(
  shipment: ShipmentDetail | undefined,
): VehiclesFormData {
  const list = shipment?.vehicles ?? []
  if (list.length === 0) return EMPTY_VEHICLES_DEFAULTS
  return { vehicles: list.map(vehicleToFormItem) }
}

function stopToFormLocation(stop: StopResponse): LocationData {
  return {
    id: stop.id,
    businessName: stop.business_name ?? '',
    address: stop.address ?? '',
    city: stop.city ?? '',
    state: stop.state ?? '',
    zip: stop.zip ?? '',
    contact: stop.contact_name ?? '',
    phone: usPhoneDigits(stop.contact_phone),
    mobile: usPhoneDigits(stop.contact_mobile_phone),
    additionalNote: stop.notes ?? '',
    type: normalizeLocationType(stop.location_type),
  }
}

export function locationToFormDefaults(
  shipment: ShipmentDetail | undefined,
): LocationFormData {
  const { origin, destination } = pickRouteEndpoints(shipment?.stops)
  return {
    origin: origin
      ? stopToFormLocation(origin)
      : { ...EMPTY_LOCATION_DEFAULTS.origin },
    destination: destination
      ? stopToFormLocation(destination)
      : { ...EMPTY_LOCATION_DEFAULTS.destination },
  }
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

function toDateOrUndefined(value: string | null | undefined): Date | undefined {
  if (!value) return undefined
  // Date-only strings ("2026-06-24") parse as UTC midnight via `new Date`,
  // which shifts to the previous day in negative timezones. Build them from
  // local calendar parts so the date picker shows the intended day.
  const dateOnly = DATE_ONLY_RE.exec(value)
  const date = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      )
    : new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function shipmentToFormDefaults(
  shipment: ShipmentDetail | undefined,
): ShipmentFormData {
  if (!shipment) return EMPTY_SHIPMENT_DEFAULTS
  return {
    trailer: shipment.transport_type,
    source: shipment.provider?.name ?? '',
    sourceId: shipment.provider?.id ?? '',
    tags: (shipment.tags ?? []).map((tag) => tag.id),
    shipDate: toDateOrUndefined(shipment.first_available_date),
    pickupDate: toDateOrUndefined(shipment.estimated_pickup_at),
    deliveryDate: toDateOrUndefined(shipment.estimated_delivery_at),
    loadBoardNote: shipment.loadboard_instructions ?? '',
    customerNote: shipment.instructions ?? '',
  }
}

export function tagsToSeedOptions(
  shipment: ShipmentDetail | undefined,
): MultiSelectOption[] {
  return (shipment?.tags ?? []).map((tag) => ({
    value: tag.id,
    label: tag.name,
  }))
}

export { EMPTY_VEHICLE }
